import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import Slider from 'rc-slider';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARK, SILVER} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export interface BaseSliderProps {
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
  step: number;
  disabled: boolean;
  className?: string;
}

interface IconProps {
  leftIcon: BenchlingIconName;
  onLeftIconClick: () => void;
  rightIcon: BenchlingIconName;
  onRightIconClick: () => void;
}

// This applies the same color as was used in a previous version of the slider.
// It can be altered, just preserving it for now to minimize the visual difference.
const JQUERYUI_ORANGE = '#f6a828';

function BaseSlider(props: BaseSliderProps & IconProps): JSX.Element {
  const {className, leftIcon, onLeftIconClick, rightIcon, onRightIconClick, disabled, ...sliderProps} = props;
  const sharedBarStyles = {border: `1px solid ${SILVER}`, cursor: disabled ? 'not-allowed' : 'pointer'};
  return (
    <div
      className={classNames(css(styles.container), className)}
      {...getTestProps({component: 'BaseSlider', element: 'root'})}
    >
      <BenchlingIcon
        icon={leftIcon}
        onClick={disabled ? undefined : onLeftIconClick}
        className={disabled ? css(styles.disabledCursor) : css(styles.pointerCursor)}
      />
      <Slider
        {...sliderProps}
        ariaLabelForHandle="Zoom level"
        className={css(styles.slider)}
        disabled={disabled}
        trackStyle={{...sharedBarStyles, backgroundColor: JQUERYUI_ORANGE}}
        railStyle={sharedBarStyles}
        handleStyle={{border: `1px solid ${GRAY_DARK}`, opacity: 1}}
      />
      <BenchlingIcon
        icon={rightIcon}
        onClick={disabled ? undefined : onRightIconClick}
        className={disabled ? css(styles.disabledCursor) : css(styles.pointerCursor)}
      />
    </div>
  );
}

export type ZoomSliderProps = BaseSliderProps & {
  zoomSmaller: () => void;
  zoomBigger: () => void;
};

export function ZoomSlider(props: ZoomSliderProps): JSX.Element {
  const {zoomSmaller, zoomBigger, ...sliderProps} = props;
  return (
    <BaseSlider
      leftIcon="minus-large"
      onLeftIconClick={zoomSmaller}
      rightIcon="plus-large"
      onRightIconClick={zoomBigger}
      {...sliderProps}
    />
  );
}

export type PanSliderProps = BaseSliderProps & {panLeft: () => void; panRight: () => void};

export function PanSlider(props: PanSliderProps): JSX.Element {
  const {panLeft, panRight, ...sliderProps} = props;
  return (
    <BaseSlider
      leftIcon="arrow-left"
      onLeftIconClick={panLeft}
      rightIcon="arrow-right"
      onRightIconClick={panRight}
      {...sliderProps}
    />
  );
}

export function AlignmentTraceSlider(props: BaseSliderProps): JSX.Element {
  const sharedBarStyles = {border: `1px solid ${SILVER}`, cursor: props.disabled ? 'not-allowed' : 'pointer'};
  return (
    <Slider
      {...props}
      vertical={true}
      className={css(styles.alignmentSlider)}
      disabled={props.disabled}
      // For some reason, rc-slider tries to position vertical tracks absolutely
      trackStyle={{...sharedBarStyles, backgroundColor: JQUERYUI_ORANGE, left: '0px'}}
      railStyle={sharedBarStyles}
      handleStyle={{border: `1px solid ${GRAY_DARK}`, opacity: 1}}
    />
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    width: '100px',
  },
  slider: {
    margin: '0px 8px',
  },
  alignmentSlider: {
    padding: '0px',
  },
  disabledCursor: {
    cursor: 'not-allowed',
  },
  pointerCursor: {
    cursor: 'pointer',
  },
});
